import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FeaturedVideo } from '@/shared/types';
import { Button, EmptyState, Spinner } from '@/shared/ui';
import { fetchShowcaseVideos } from '@/features/browse/services/showcaseService';
import { StyleSection } from '@/features/landing/components/StyleSection';
import { buildStyleSections } from '@/features/landing/services/styleSections';

/**
 * Every section, capped as on the landing page — the index the rail's Templates
 * item opens.
 *
 * Deliberately the same bands rather than one flat wall: the sections are how the
 * catalogue is organised, and a visitor arriving from the rail should see the same
 * shape they saw below the landing hero. What is missing here is the stage and the
 * NOW SHOWING hero, because this is a page about browsing rather than a pitch.
 *
 * A band's own "See all" still narrows to `/showcase/:style` from here, so the two
 * levels are index -> section rather than two views of the same list.
 */
export default function TemplatesPage() {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<FeaturedVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    void fetchShowcaseVideos()
      .then(setVideos)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const bands = useMemo(() => buildStyleSections(videos), [videos]);
  const inviteSignIn = () => navigate('/login');

  return (
    <section className="showcase-all">
      <header className="showcase-all__head">
        <h1 className="showcase-all__title">Templates</h1>
        <p className="showcase-all__blurb">
          Every video we have made, grouped by how it was rendered.
        </p>
      </header>

      {loading ? (
        <div className="showcase-all__state">
          <Spinner size={26} label="Loading videos" />
        </div>
      ) : loadError ? (
        <div className="showcase-all__state" role="alert">
          <EmptyState
            headingLevel={2}
            title="Couldn’t load these videos"
            hint="Please check your connection and try again."
            action={
              <Button variant="secondary" onClick={load}>
                Try again
              </Button>
            }
          />
        </div>
      ) : bands.length === 0 ? (
        <div className="showcase-all__state">
          <EmptyState
            headingLevel={2}
            title="Nothing to show yet"
            hint="Check back soon for new videos."
          />
        </div>
      ) : (
        bands.map((band) => (
          <StyleSection
            key={band.key}
            section={band}
            onSelect={inviteSignIn}
            onToggleSave={inviteSignIn}
            onSeeAll={(section) => navigate(`/showcase/${section.key}`)}
          />
        ))
      )}
    </section>
  );
}
